/**
 * The programme vocabulary, in one place.
 *
 * A school runs three programmes — PYP in the lower school, IHP and DP above it
 * — and two words modify them: 'Both' for a student taking IHP and DP courses
 * side by side, and 'Any' for a configuration row that applies whatever
 * programme is asked of it. Five words, and every `program_type` column in the
 * schema holds one of them.
 *
 * **Not every column may hold all five, and that is the whole reason this file
 * exists.** The subsets below are genuinely different — a student is never
 * 'Any', a grade-scale band is never 'PYP' — but they were expressed by writing
 * the words out again at each site, about twenty times: six inline enums in
 * `db/schemas/pg.ts`, four TypeScript unions, three zod enums, two frontend
 * lists, and two exported consts both named `PROGRAM_TYPES` that held
 * *different values* (`import/coursePlan.ts` the selector list,
 * `utils/csvParser.ts` the student list). Nothing tied them together, so
 * `students.program_type` offered 'PYP' where `grade_scales.program_type`
 * offered 'Any' and no type noticed.
 *
 * Now there is one vocabulary and three named subsets of it. Widening or
 * narrowing a subset is a single edit here, and `programs.test.ts` plus
 * `db/schemas/programVocabulary.test.ts` fail if a column drifts from one.
 *
 * Note the names: none of them is `ProgramType`. That was the name of the
 * *columns*, reused for one of the subsets, which is how the two came to be
 * confused in the first place. The columns keep their name; the types get
 * accurate ones.
 */

/**
 * The programmes themselves — the ones a child is actually in. Ordered lower
 * school first, which is the order a report card's programme bands run in.
 */
export const PROGRAMS = ['PYP', 'IHP', 'DP'] as const;
export type Program = (typeof PROGRAMS)[number];

/**
 * Every word the vocabulary has: the three programmes and the two modifiers.
 * Nothing outside this list may be written to a `program_type` column.
 */
export const PROGRAM_VOCABULARY = ['PYP', 'IHP', 'DP', 'Both', 'Any'] as const;
export type ProgramWord = (typeof PROGRAM_VOCABULARY)[number];

/**
 * What a *student record* may say — `students.program_type`.
 *
 * 'Both' is real here: a student genuinely sits in IHP and DP courses at once.
 * 'Any' is not — a child is in a programme, and "whichever" is not an answer a
 * registrar can give. Ordered as the student form and the roster filter list
 * them, which is upper school first because that is where most of the roll is.
 */
export const STUDENT_PROGRAMS = ['IHP', 'DP', 'Both', 'PYP'] as const;
export type StudentProgram = (typeof STUDENT_PROGRAMS)[number];

/**
 * What a *configuration row* may target — `courses.program_type`,
 * `category_templates.program_type` and `grade_scales.program_type`.
 * (`course_credits.program_type` was a fourth until migration `0036` dropped
 * that table; nothing had ever written a row to it.)
 *
 * 'Any' is the fallback every programme resolves through, and is what makes a
 * school with one uniform set of weights or bands expressible without listing
 * them per programme. 'PYP' is absent deliberately: a PYP result is a
 * descriptor arrived at by judgement, not a percentage read off a band, so
 * there is nothing for a PYP-specific band to say. See {@link scaleProgramFor}.
 *
 * This list used to be spelled 'Standard' rather than 'Any', which collided
 * with `courses.level = 'Standard'` in the adjacent column — and, inside
 * `level`, sat next to 'SL', which *is* Standard Level. Migration 0010 renamed
 * the data; `programs.test.ts` keeps the two vocabularies from sharing a word
 * again.
 */
export const PROGRAM_SELECTORS = ['IHP', 'DP', 'Both', 'Any'] as const;
export type ProgramSelector = (typeof PROGRAM_SELECTORS)[number];

/**
 * What a report-card template is banded by —
 * `report_card_templates.program_band`.
 *
 * The concrete programmes and neither modifier: a card is printed for one
 * programme's children in one programme's format. 'Both' would name no layout
 * and 'Any' no audience.
 */
export const PROGRAM_BANDS = PROGRAMS;
export type ProgramBand = Program;

/** The long form, for a picker with room for it. */
export const PROGRAM_LABELS: Record<ProgramWord, string> = {
  PYP: 'PYP (Primary Years, lower school)',
  IHP: 'IHP (International Honors Program)',
  DP: 'DP (Diploma Programme)',
  Both: 'Both Programs',
  Any: 'Any Program',
};

/**
 * Which set of grade-scale bands a student's programme resolves against — the
 * one bridge between {@link STUDENT_PROGRAMS} and {@link PROGRAM_SELECTORS}.
 *
 * It is one function rather than three inline ternaries because the callers
 * must agree. The term lock, the grade-change approval and the entered override
 * all turn a percentage into the same permanent record; if the lock resolved a
 * PYP student through 'Any' while the approval refused them, a locked grade
 * would become uncorrectable — one row with two disagreeing derivations, which
 * is AR-06/07's failure class in a new place.
 */
export function scaleProgramFor(
  program: StudentProgram | null | undefined
): ProgramSelector {
  if (!program || program === 'PYP') return 'Any';
  return program;
}

export function isProgram(value: string): value is Program {
  return (PROGRAMS as readonly string[]).includes(value);
}

export function isStudentProgram(value: string): value is StudentProgram {
  return (STUDENT_PROGRAMS as readonly string[]).includes(value);
}

export function isProgramSelector(value: string): value is ProgramSelector {
  return (PROGRAM_SELECTORS as readonly string[]).includes(value);
}
